package wat.ui

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*
import wat.service.DataService
import kotlin.js.Date

object DiaryTab {
    private var searchQuery = ""
    private var sortAsc = false

    private val VIBES = listOf(
        Triple(1, "#fb7185", "\uD83D\uDE29"),  // Zle
        Triple(2, "#fbbf24", "\uD83D\uDE14"),  // Slabo
        Triple(3, "#a78bfa", "\uD83D\uDE10"),  // OK
        Triple(4, "#38bdf8", "\uD83D\uDE0A"),  // Dobre
        Triple(5, "#2dd4bf", "\uD83E\uDD29")   // Super
    )

    private fun vibeInfo(mood: Int?): Triple<Int, String, String>? {
        if (mood == null || mood == 0) return null
        return VIBES.find { it.first == mood }
    }

    private fun getDayName(dateStr: String): String {
        val d = Date(dateStr)
        return when (d.getDay()) {
            0 -> t("day_sun"); 1 -> t("day_mon"); 2 -> t("day_tue")
            3 -> t("day_wed"); 4 -> t("day_thu"); 5 -> t("day_fri")
            6 -> t("day_sat"); else -> ""
        }
    }

    fun render(container: HTMLDivElement, userId: String, showForm: Boolean, onSave: () -> Unit) {
        if (showForm) {
            container.appendChild(renderForm(userId, onSave))
            return
        }

        val entries = DataService.diaryEntries

        if (entries.isEmpty()) {
            container.appendChild(div {
                style.cssText = "text-align:center;padding:48px 20px"
                appendChild(span("\uD83D\uDCD6") { style.cssText = "font-size:36px;display:block;margin-bottom:12px;opacity:0.25" })
                appendChild(div {
                    style.cssText = "font-size:16px;font-weight:700;color:${Styles.TEXT_30};margin-bottom:6px"
                    textContent = t("diary_no_entries")
                })
                appendChild(div {
                    style.cssText = "font-size:13px;color:${Styles.TEXT_15}"
                    textContent = t("diary_no_entries_sub")
                })
            })
            return
        }

        // Search bar
        if (entries.size > 3) {
            container.appendChild(div {
                style.cssText = "display:flex;gap:8px;margin-bottom:16px"
                appendChild(input("text") {
                    style.cssText = "flex:1;padding:10px 14px;border-radius:10px;border:none;background:${Styles.TEXT_05};color:${Styles.TEXT};font-size:13px;font-weight:500;outline:none;box-sizing:border-box;font-family:inherit"
                    placeholder = t("diary_search_placeholder")
                    value = searchQuery
                    oninput = { e ->
                        searchQuery = (e.target as HTMLInputElement).value
                        rebuildList(container, entries, userId, onSave)
                        Unit
                    }
                })
                appendChild(button(if (sortAsc) "\u2191" else "\u2193") {
                    style.cssText = "width:40px;height:40px;border-radius:10px;border:none;background:${Styles.TEXT_05};color:${Styles.TEXT_40};font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center"
                    onclick = {
                        sortAsc = !sortAsc
                        val sy = window.scrollY
                        MainApp.renderPage()
                        window.requestAnimationFrame { window.scrollTo(0.0, sy) }
                        Unit
                    }
                })
            })
        }

        val listBox = div { id = "diary-list" }
        rebuildList(listBox, entries, userId, onSave)
        container.appendChild(listBox)
    }

    private fun rebuildList(box: HTMLDivElement, entries: List<DiaryEntry>, userId: String, onSave: () -> Unit) {
        val target = document.getElementById("diary-list") as? HTMLDivElement ?: box
        target.clear()

        val filtered = if (searchQuery.isBlank()) entries
            else entries.filter { it.body.lowercase().contains(searchQuery.lowercase()) }
        val sorted = if (sortAsc) filtered.sortedBy { it.date } else filtered.sortedByDescending { it.date }

        if (sorted.isEmpty() && searchQuery.isNotBlank()) {
            target.appendChild(div {
                style.cssText = "text-align:center;padding:32px 16px;color:${Styles.TEXT_20};font-size:14px"
                textContent = t("diary_no_results")
            })
            return
        }

        val monthNames = listOf("", "Január", "Február", "Marec", "Apríl", "Máj", "Jún", "Júl", "August", "September", "Október", "November", "December")
        var lastMonth = ""
        sorted.forEachIndexed { idx, entry ->
            // Month separator
            val month = entry.date.take(7)
            if (month != lastMonth) {
                lastMonth = month
                val parts = month.split("-")
                val mIdx = parts.getOrNull(1)?.toIntOrNull() ?: 0
                val mName = if (mIdx in 1..12) monthNames[mIdx] else ""
                target.appendChild(div {
                    style.cssText = "padding:${if (idx == 0) "4px" else "16px"} 0 6px;display:flex;align-items:center;gap:8px"
                    appendChild(div { style.cssText = "flex:1;height:1px;background:${Styles.YELLOW}30" })
                    appendChild(span("$mName ${parts[0]}") {
                        style.cssText = "font-size:11px;font-weight:700;color:${Styles.YELLOW};letter-spacing:1px"
                    })
                })
            }
            val vi = vibeInfo(entry.mood)

            target.appendChild(div {
                style.cssText = "${Styles.CARD};margin-bottom:10px;padding:14px 16px"

                // Header: date + vibe dots + delete
                appendChild(div {
                    style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:6px"

                    // Date
                    appendChild(span(formatDateFull(entry.date)) {
                        style.cssText = "font-size:12px;font-weight:700;color:${Styles.TEXT_SEC}"
                    })

                    // Vibe dots
                    if (vi != null) {
                        appendChild(div {
                            style.cssText = "display:flex;gap:3px;align-items:center;margin-left:4px"
                            for (d in 1..5) {
                                appendChild(div {
                                    style.cssText = "width:6px;height:6px;border-radius:3px;background:${if (d <= vi.first) vi.second else "${Styles.TEXT_10}"}"
                                })
                            }
                        })
                    }

                    appendChild(div { style.cssText = "flex:1" })

                    // Delete
                    appendChild(button("\u2715") {
                        style.cssText = "width:28px;height:28px;border-radius:8px;border:1.5px solid ${Styles.CARD_BORDER};background:${Styles.INPUT_BG};color:${Styles.TEXT_40};font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                        var confirmMode = false
                        onclick = {
                            if (confirmMode) {
                                GlobalScope.launch {
                                    DataService.deleteDiaryEntry(entry.id)
                                    DataService.loadDiaryEntries(userId)
                                    onSave()
                                }
                            } else {
                                confirmMode = true
                                this.style.cssText = "height:28px;padding:0 10px;border-radius:8px;border:1.5px solid #fb718540;background:#fb718518;color:#fb7185;font-size:11px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-family:inherit"
                                this.textContent = "delete?"
                                val btn = this
                                window.setTimeout({
                                    if (confirmMode) {
                                        confirmMode = false
                                        btn.style.cssText = "width:28px;height:28px;border-radius:8px;border:1.5px solid ${Styles.CARD_BORDER};background:${Styles.INPUT_BG};color:${Styles.TEXT_40};font-size:13px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;flex-shrink:0"
                                        btn.textContent = "\u2715"
                                    }
                                }, 3000)
                            }
                            Unit
                        }
                    })
                })

                // Body text
                appendChild(div {
                    style.cssText = "font-size:14px;color:${Styles.TEXT};line-height:1.6;white-space:pre-wrap;word-break:break-word"
                    textContent = entry.body
                })
            })
        }
    }

    // ─── ADD FORM with vibe selector ──────────────────────────────────
    private fun renderForm(userId: String, onSave: () -> Unit): HTMLElement {
        var formDate = todayLocal()
        var formText = ""
        var formMood: Int? = null

        return div {
            style.cssText = Styles.CARD

            // Title
            appendChild(div {
                style.cssText = "font-size:15px;font-weight:800;color:${Styles.TEXT};margin-bottom:20px;display:flex;align-items:center;gap:8px"
                appendChild(span("\u270F\uFE0F") { style.cssText = "font-size:16px" })
                appendChild(span(t("diary_add_entry")))
            })

            // Date
            appendChild(div {
                style.cssText = "margin-bottom:14px"
                appendChild(label(t("diary_label_date")) {
                    style.cssText = "font-size:11px;font-weight:700;color:${Styles.TEXT_30};display:block;margin-bottom:6px;text-transform:uppercase;letter-spacing:1.5px"
                })
                appendChild(input("date") {
                    style.cssText = "width:100%;padding:10px 14px;border-radius:10px;border:none;background:${Styles.TEXT_06};color:${Styles.TEXT};font-size:14px;font-weight:600;outline:none;box-sizing:border-box;font-family:inherit;color-scheme:${Styles.COLOR_SCHEME}"
                    value = formDate
                    max = todayLocal()
                    oninput = { e -> formDate = (e.target as HTMLInputElement).value; Unit }
                })
            })

            // Text area
            appendChild(div {
                style.cssText = "margin-bottom:16px"
                appendChild(textarea {
                    style.cssText = "width:100%;padding:14px;border-radius:10px;border:none;background:${Styles.TEXT_06};color:${Styles.TEXT};font-size:15px;font-family:inherit;outline:none;box-sizing:border-box;resize:vertical;min-height:140px;line-height:1.6"
                    placeholder = t("diary_entry_placeholder")
                    rows = 6
                    oninput = { e -> formText = (e.target as HTMLTextAreaElement).value; Unit }
                })
            })

            // Mood selector - numbered circles
            appendChild(div {
                style.cssText = "margin-bottom:20px;display:flex;gap:8px;justify-content:center"
                VIBES.forEach { (v, c, _) ->
                    appendChild(div {
                        id = "mood-btn-$v"
                        val sel = formMood == v
                        style.cssText = "width:36px;height:36px;border-radius:18px;background:${if (sel) c else "${Styles.TEXT_06}"};color:${if (sel) "${Styles.TEXT}" else "${Styles.TEXT_30}"};font-size:15px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:inherit"
                        textContent = "$v"
                        onclick = {
                            formMood = if (formMood == v) null else v
                            // Update all buttons
                            VIBES.forEach { (bv, bc, _) ->
                                val btn = kotlinx.browser.document.getElementById("mood-btn-$bv") as? HTMLElement
                                val s = formMood == bv
                                btn?.style?.background = if (s) bc else Styles.TEXT_06
                                btn?.style?.color = if (s) Styles.TEXT else Styles.TEXT_30
                            }
                            Unit
                        }
                    })
                }
            })

            // Buttons
            appendChild(div {
                style.cssText = "display:flex;gap:10px"
                appendChild(button(t("diary_btn_save")) {
                    style.cssText = "flex:1;padding:14px;border-radius:12px;border:none;${Styles.BTN_ACTION2};font-size:15px;font-weight:700;cursor:pointer"
                    onclick = {
                        if (formText.isNotBlank() && formDate <= todayLocal()) {
                            GlobalScope.launch {
                                DataService.saveDiaryEntry(userId, DiaryEntry(
                                    userId = userId, date = formDate,
                                    body = formText.trim(),
                                    mood = formMood
                                ))
                                DataService.loadDiaryEntries(userId)
                                onSave()
                            }
                        }
                        Unit
                    }
                })
                appendChild(button(t("diary_btn_cancel")) {
                    style.cssText = Styles.BTN_SECONDARY
                    onclick = { onSave() }
                })
            })
        }
    }
}
